// src/pages/shippment/MainTable.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import CommonDataTable from "../../components/CommonDataTable";
import {
    IconButton,
    Menu,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Grid,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { ALL_FIELDS } from "./formFields/salesOrderSchema.js";
import { fetchSalesOrder, refreshSalesOrderWms } from "../../services/salesOrder/salesOrder"; // adjust path if needed
import { toast } from "../../components/AppToast";

import { useTranslation } from "react-i18next";

function RowActionMenu({ params, onEditRow, onDeleteRow, onViewRow }) {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);
    const handleClick = (e) => setAnchorEl(e.currentTarget);
    const handleClose = () => setAnchorEl(null);

    if (!onEditRow && !onDeleteRow && !onViewRow) return null;
    const row = params?.row ?? {};

    return (
        <>
            <IconButton size="small" onClick={handleClick}>
                <MoreVertIcon />
            </IconButton>
            <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                {onViewRow && (
                    <MenuItem
                        onClick={() => {
                            onViewRow?.(row);
                            handleClose();
                        }}
                    >
                        <i className="bi bi-eye me-2" /> View
                    </MenuItem>
                )}
                {onEditRow &&
                    row?.source === "INTERNAL" &&
                    !["In Process", "Completed"].includes(row?.status) && (
                        <MenuItem
                            onClick={() => {
                                onEditRow?.(row);
                                handleClose();
                            }}
                        >
                            <i className="bi bi-pencil me-2" /> Edit
                        </MenuItem>
                    )
                }

            </Menu>
        </>
    );
}

const actionsCol = (onEditRow, onDeleteRow, onViewRow) => ({
    field: "actions",
    headerName: "",
    width: 60,
    sortable: false,
    filterable: false,
    renderCell: (params) => (
        <RowActionMenu
            params={params}
            onEditRow={onEditRow}
            onDeleteRow={onDeleteRow}
            onViewRow={onViewRow}
        />
    ),
});

// ---- Date helpers ----
const formatDateTimeIST = (value) => {
    if (!value) return "";
    try {
        const d = new Date(value);
        if (isNaN(d.getTime())) return String(value);
        return d.toLocaleString("en-IN", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
            timeZone: "Asia/Kolkata",
        });
    } catch {
        return String(value);
    }
};


const formatDateIST = (value) => {
    if (!value) return "";
    try {
        const d = new Date(value);
        if (isNaN(d.getTime())) return String(value);
        // dd/mm/yyyy (India style). Agar mm-dd-yyyy chahiye to en-US use karna.
        return d.toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" });
    } catch {
        return String(value);
    }
};



export default function MainTable({
    onEdit,
    onEditRow,
    onViewRow: parentOnViewRow,
    onToggleCreateVisibility,
    showCreateButton = false,

    createButtonIcon = "bi-plus-lg",
    onCreateClick,
    initialRows = null,
    onOpenShipment,
}) {

    const { t, i18n } = useTranslation();
    const [rows, setRows] = useState(initialRows ?? []);
    const rowsRef = useRef([]);
    const [loading, setLoading] = useState(false);
    const abortRef = useRef(null);
    const rafRef = useRef(null);

    const [viewOpen, setViewOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);

    // Build columns from schema, fix headers, and ensure createdAt/updatedAt present
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0);


    // const ALL_COLUMNS = useMemo(() => {
    //     const cols = (ALL_FIELDS || [])
    //         .filter(f =>
    //             f.name !== "orderDate" &&
    //             f.name !== "createdAt" &&
    //             f.name !== "updatedAt"
    //         )
    //         .map((f) => {
    //             let header = f.label || f.name;
    //             if (f.name === "soNumber") header = "Sales Order";

    //             const col = { field: f.name, headerName: header };

    //             // agar schema me type info ho to map kar sakte ho
    //             if (f.type === "number") col.type = "number";
    //             if (f.type === "date") col.type = "date";

    //             return col;
    //         });

    //     const ensure = (field, headerName, extra = {}) => {
    //         if (!cols.some((c) => c.field === field)) {
    //             cols.push({ field, headerName, ...extra });
    //         }
    //     };



    //     ensure("source", "Source");
    //     ensure("createdDate", "Created Date");
    //     ensure("updatedDate", "Updated Date");
    //     ensure("shipmentHistory", "Shipments");
    //     ensure("status", "Status");

    //     return cols;
    //     // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [ALL_FIELDS]);



    // initial visible columns


    // inside MainTable component: ensure you have `const { t, i18n } = useTranslation();`


    // const createButtonText = "Create New SO/STO";

    const statusToLabel = (v) => {
        if (v === -1 || `${v}` === "-1") return "UNKNOWN";
        if (v === -2 || `${v}` === "-2") return "OUT OF SYNC";
        
        if (v === 0 || `${v}` === "00") return "EMPTY ORDER";
        if (v === 2 || `${v}` === "02") return "CREATED EXTERNALLY";
        if (v === 4 || `${v}` === "04") return "CREATED INTERNALLY";
        if (v === 6 || `${v}` === "06") return "DID NOT ALLOCATE";
        if (v === 8 || `${v}` === "08") return "CONVERTED";
        if (v === 9 || `${v}` === "09") return "NOT STARTED";
        if (v === 10 || `${v}` === "10") return "BATCHED";
        if (v === 11 || `${v}` === "11") return "PART PRE-ALLOCATED";
        if (v === 12 || `${v}` === "12") return "PRE-ALLOCATED";
        if (v === 13 || `${v}` === "13") return "RELEASED TO WAREHOUSE PLANNER";
        if (v === 14 || `${v}` === "14") return "PART ALLOCATED";
        if (v === 15 || `${v}` === "15") return "PART ALLOCATED / PART PICKED";
        if (v === 16 || `${v}` === "16") return "PART ALLOCATED / PART SHIPPED";
        if (v === 17 || `${v}` === "17") return "ALLOCATED";
        if (v === 18 || `${v}` === "18") return "SUBSTITUTED";
        if (v === 22 || `${v}` === "22") return "PART RELEASED";
        if (v === 25 || `${v}` === "25") return "PART RELEASED / PART PICKED";
        if (v === 27 || `${v}` === "27") return "PART RELEASED / PART SHIPPED";
        if (v === 29 || `${v}` === "29") return "RELEASED";
        if (v === 51 || `${v}` === "51") return "IN PICKING";
        if (v === 52 || `${v}` === "52") return "PART PICKED";
        if (v === 53 || `${v}` === "53") return "PART PICKED / PART SHIPPED";
        if (v === 55 || `${v}` === "55") return "PICKED COMPLETE";
        if (v === 57 || `${v}` === "57") return "PICKED / PART SHIPPED";
        if (v === 61 || `${v}` === "61") return "IN PACKING";
        if (v === 68 || `${v}` === "68") return "PACK COMPLETE";
        if (v === 75 || `${v}` === "75") return "STAGED";
        if (v === 78 || `${v}` === "78") return "MANIFESTED";
        if (v === 82 || `${v}` === "82") return "IN LOADING";
        if (v === 88 || `${v}` === "88") return "LOADED";
        if (v === 92 || `${v}` === "92") return "PART SHIPPED";
        if (v === 94 || `${v}` === "94") return "CLOSE PRODUCTION";
        if (v === 95 || `${v}` === "95") return "SHIPPED COMPLETE";
        if (v === 96 || `${v}` === "96") return "DELIVERED ACCEPTED";
        if (v === 97 || `${v}` === "97") return "DELIVERED REJECTED";
        if (v === 98 || `${v}` === "98") return "CANCELLED EXTERNALLY";
        if (v === 99 || `${v}` === "99") return "CANCELLED INTERNALLY";
        
        // ORDRSTATUS mappings
        if (`${v}` === "0") return "NORMAL";
        if (`${v}` === "1") return "RELEASED";
        if (`${v}` === "3") return "IN PROCESS";
        if (`${v}` === "4") return "CLOSED";
        if (`${v}` === "5") return "PICKED/SORTED";
        if (`${v}` === "6") return "PACKED";
        if (`${v}` === "7") return "SORTED";
        if (`${v}` === "8") return "LOADED";
        if (`${v}` === "9") return "SHIPPED";
        if (typeof v === "string" && v.trim() !== "") return v.toUpperCase();
        return "";
    };
   
   

    const createButtonText = useMemo(() => t(`salesOrder.buttons.create`), [t]);

    const ALL_COLUMNS = useMemo(() => {
        const cols = (ALL_FIELDS || [])
            .filter(
                (f) =>
                    f.name !== "orderDate" &&
                    f.name !== "createdAt" &&
                    f.name !== "updatedAt"
            )
            .map((f) => {
                // priority:
                // 1) schema labelKey -> t(labelKey)
                // 2) translation key salesOrder.table.<name>
                // 3) schema label
                // 4) field name
                let header = "";

                if (f.labelKey) {
                    header = t(f.labelKey, { defaultValue: f.label || f.name });
                } else {
                    header = t(`salesOrder.table.${f.name}`, { defaultValue: f.label || f.name });
                }

                // special-case fallback for soNumber naming
                if (f.name === "soNumber" && (!f.labelKey)) {
                    header = t("salesOrder.table.soNumber", { defaultValue: f.label || "Sales Order" });
                }

                const col = { field: f.name, headerName: header };

                if (f.type === "number") col.type = "number";
                if (f.type === "date") col.type = "date";

                return col;
            });

        const ensure = (field, headerKeyOrText, extra = {}) => {
            if (!cols.some((c) => c.field === field)) {
                const header =
                    typeof headerKeyOrText === "string" && headerKeyOrText.includes(".")
                        ? t(headerKeyOrText, { defaultValue: headerKeyOrText.split(".").pop() })
                        : t(headerKeyOrText, { defaultValue: headerKeyOrText });
                cols.push({ field, headerName: header, ...extra });
            }
        };

        ensure("source", "salesOrder.table.source");
        ensure("createdDate", "salesOrder.table.createdDate");
        ensure("updatedDate", "salesOrder.table.updatedDate");
        ensure("shipmentHistory", "salesOrder.table.shipmentHistory");
        ensure("status", "salesOrder.table.status");

        return cols;
    }, [ALL_FIELDS, t, i18n.language]);

    const INITIAL_VISIBLEOLD = [
        "actions",
        "soNumber",
        "owner",
        "orderType",
        "expectedReceiptDate",
        "sourceLocationName",
        "source",
        "status",
        "systemStatus",
        "erpStatus",
        "shipmentHistory",
        "totalOrdered",
        "openQuantity",
        "orderDate",


        "createdDate",
        "updatedDate",
    ];

    const INITIAL_VISIBLE = [
        "actions",
        "soNumber",
        "orderType",
        "owner",
        "source",
        "status",
        "systemStatus",
        "erpStatus",
        "sourceLocationName",
        "destinationLocationName",
        "totalOrdered",
        "shipmentHistory",
        "expectedReceiptDate",
        "createdDate",
    ];


    // robust mapper returning required row shape
    const mapApiItemToRow = (item) => {
        const so = item.soDetails || {};
        const sourceRaw = item.sourceDetail ?? item.source ?? {};
        const source = sourceRaw && typeof sourceRaw === "object" ? sourceRaw : {};
        const carrier = item.carrier && typeof item.carrier === "object" ? item.carrier : {};
        const dest = item.destination || {};
        const invoice = item.invoice || {};
        const lineItems = Array.isArray(item.lineItems) ? item.lineItems : [];
        const statusRaw =
            so.status ??
            item.status ??
            so.currentStatus ??
            item.currentStatus ??
            so.state ??
            item.state ??
            "";

        // ✅ convert numeric status → label
        const statusDisplay = statusToLabel(statusRaw);

        const erpStatusRaw =
            so.erpStatus ??
            so.erp_status ??
            item.erpStatus ??
            so.externalOrderStatus ??
            item.externalOrderStatus ??
            so.orderStatusErp ??
            "";
        const erpStatusDisplay =
            erpStatusRaw === "" || erpStatusRaw == null
                ? ""
                : typeof erpStatusRaw === "number" ||
                  (typeof erpStatusRaw === "string" && /^\d+$/.test(String(erpStatusRaw).trim()))
                  ? statusToLabel(erpStatusRaw)
                  : String(erpStatusRaw).trim() !== ""
                    ? String(erpStatusRaw).toUpperCase()
                    : "";

        // compute totalOrdered robustly
        let totalOrdered = 0;
        if (typeof so.totalOrdered === "number" && so.totalOrdered > 0) {
            totalOrdered = so.totalOrdered;
        } else if (
            typeof so.totalOrderedQuantity === "number" &&
            so.totalOrderedQuantity > 0
        ) {
            totalOrdered = so.totalOrderedQuantity;
        } else {
            totalOrdered = lineItems.reduce((sum, li) => {
                const d = li.detail ?? li;
                const qty = Number(
                    d?.orderedQty ?? d?.orderedQuantity ?? d?.orderQty ?? 0
                );
                return sum + (isNaN(qty) ? 0 : qty);
            }, 0);
        }

        const orderDateRaw =
            so.orderDate ??
            so.order_date ??
            item.orderDate ??
            item.order_date ??
            item.createdAt ??
            null;

        const expectedReceiptDateRaw =
            so.expectedReceiptDate ?? so.expected_receipt ?? null;

        const itemsNames = (lineItems || [])
            .map((li) => {
                const d = li.detail ?? li;
                return (d.description ?? d.skuId ?? d.sku ?? "").toString().trim();
            })
            .filter(Boolean)
            .join(", ");

        // shipmentHistory: comma-separated shipment numbers (if any)
        // compute stepwise to avoid mixing '||' and '??' in same expression
        let shipmentHistoryArr = [];
        if (Array.isArray(item.shipmentHistory)) {
            shipmentHistoryArr = item.shipmentHistory;
        } else if (item && item.shipmentHistory) {
            shipmentHistoryArr = item.shipmentHistory;
        } else {
            shipmentHistoryArr = [];
        }
        const shipmentHistoryStr = Array.isArray(shipmentHistoryArr)
            ? shipmentHistoryArr.map(s => (s?.shipmentNumber ?? s?.shipment ?? '')).filter(Boolean).join(", ")
            : '';

        const openQuantity =
            so.openQuantity ??
            so.openQty ??
            item.openQuantity ??
            lineItems.reduce((sum, li) => {
                const d = li.detail ?? li;
                const oq = Number(d?.openQty ?? d?.openQuantity ?? 0);
                return sum + (isNaN(oq) ? 0 : oq);
            }, 0);


        // ⬇️ orderDateRaw already hai (uparse code me)
        const createdRaw = item.createdAt ?? so.createdAt ?? orderDateRaw ?? null;
        const updatedRaw = item.updatedAt ?? so.updatedAt ?? null;

        const base = {
            id: item._id ?? so.soNumber ?? item.soNumber ?? String(Date.now()),
            _id: item._id ?? null,
            _raw: item,

            // identity
            soNumber: so.soNumber ?? item.soNumber ?? "",
            owner: so.owner ?? item.owner ?? "",
            orderType: so.orderType ?? item.orderType ?? "",
            status: statusDisplay,
            systemStatus: statusDisplay,
            erpStatus: erpStatusDisplay,
            // dates (IST with seconds)
            orderDate: formatDateTimeIST(orderDateRaw),
            expectedReceiptDate: formatDateIST(expectedReceiptDateRaw),
            invoiceDate: formatDateTimeIST(invoice.invoiceDate ?? invoice.createdAt),

            // shipment/order source
            source:
                (typeof so.source === "string" && so.source) ||
                source.sourceCode ||
                source.code ||
                (typeof source.source === "string" ? source.source : "") ||
                "",

            totalOrdered,
            totalOrderLines: lineItems.length,

            // Source mapping
            sourceLocationName:
                source.sourceName ?? source.locationName ?? source.name ?? "",
            carrierCode: carrier.carrierCode ?? "",
            carrierName: carrier.carrierName ?? "",
            carrierAddress1: carrier.addressLine1 ?? carrier.carrierAddress1 ?? "",
            carrierAddress2: carrier.addressLine2 ?? carrier.carrierAddress2 ?? "",
            carrierCity: carrier.city ?? carrier.carrierCity ?? "",
            carrierState: carrier.state ?? carrier.carrierState ?? "",
            carrierCountry: carrier.country ?? carrier.carrierCountry ?? "",
            carrierPhone: carrier.phone ?? carrier.carrierPhone ?? "",
            carrierTrailerNumber: so.trailerNumber ?? "",
            carrierTrailerOwner: so.trailerOwner ?? "",
            carrierDriverName: so.driverName ?? "",
            departureDateTime: so.departureDateTime ?? "",
            actualDeliveryDate: so.actualDeliveryDate ?? "",
            sourceAddress1: source.addressLine1 ?? source.address1 ?? "",
            sourceAddress2: source.addressLine2 ?? source.address2 ?? "",
            sourceCity: source.city ?? "",
            sourceState: source.state ?? "",
            sourceCountry: source.country ?? "",
            sourceContact: source.contact ?? "",
            sourceEmail: source.email ?? "",

            // Destination
            destinationLocationName: dest.locationName ?? dest.name ?? "",
            destinationAddress1: dest.addressLine1 ?? dest.address1 ?? "",
            destinationAddress2: dest.addressLine2 ?? dest.address2 ?? "",
            destinationCity: dest.city ?? "",
            destinationState: dest.state ?? "",
            destinationCountry: dest.country ?? "",
            destinationContact: dest.contact ?? "",
            destinationEmail: dest.email ?? "",

            // BillTo
            billToName:
                (item.billTo ?? {}).billToName ?? (item.billTo ?? {}).name ?? "",

            // Invoice
            invoiceNumber: invoice.invoiceNumber ?? "",

            // lineItems flattened
            lineItems: lineItems.map((li) => li.detail ?? li),

            // derived
            items: itemsNames,
            openQuantity,
            shipmentHistory: shipmentHistoryStr,
            // Attach record timestamps (IST)

            // ✅ keep ONLY these two date columns (DATE+TIME)
            createdDate: formatDateTimeIST(createdRaw),
            updatedDate: formatDateTimeIST(updatedRaw),
            // createdAt: formatDateTimeIST(item.createdAt ?? so.createdAt),
            // updatedAt: formatDateTimeIST(item.updatedAt ?? so.updatedAt),
        };

        // Ensure every schema field exists on row (fallback "")
        try {
            (ALL_FIELDS || []).forEach((f) => {
                if (!(f.name in base)) base[f.name] = "";
            });
        } catch {
            // ignore
        }

        return base;
    };


    // src/schema/salesOrderFieldMap.js (ya jahan bhi rakhna ho)

    // 🔹 UI columnId -> backend field path (Sales Order)
    const SALES_ORDER_FILTER_FIELD_MAP = {
        // Action column (no backend field)
        __actions: null,

        // ---------- Header / basic info ----------
        soNumber: "soDetails.soNumber",
        owner: "soDetails.owner",
        orderType: "soDetails.orderType",

        // Qty / totals
        totalOrderedQuantity: "soDetails.totalOrderedQuantity",
        totalOrderQuantity: "soDetails.totalOrderedQuantity",
        totalOrderLines: "soDetails.totalOrderLines",         // assumption

        orderDate: "soDetails.orderDate",
        expectedReceiptDate: "soDetails.expectedReceiptDate",

        // External number
        externalOrder1: "soDetails.externalSoNumber",

        Status: "status",
        status: "status",
        systemStatus: "status",
        erpStatus: "soDetails.erpStatus",

        // Open Qty / weight / volume / source
        openQuantity: "soDetails.openQuantity",
        totalWeight: "soDetails.totalWeight",
        totalVolume: "soDetails.totalVolume",
        source: "soDetails.source",

        // Root audit fields
        createdBy: "createdBy",
        updatedBy: "updatedBy",
        createdAt: "createdAt",
        updatedAt: "updatedAt",

        // misc block ke date fields
        createdDate: "createdAt",
        updatedDate: "updatedAt",

        // ---------- Source Address (sourceDetail) ----------

        sourceLocationName: "sourceDetail.locationName",
        sourceAddress1: "sourceDetail.addressLine1",
        sourceAddress2: "sourceDetail.addressLine2", // assumption
        sourceAddress3: "sourceDetail.addressLine3", // assumption
        sourceAddress4: "sourceDetail.addressLine4", // assumption
        sourceCity: "sourceDetail.city",
        sourceState: "sourceDetail.state",
        sourceCountry: "sourceDetail.country",
        sourceContact: "sourceDetail.contact",       // assumption
        sourceEmail: "sourceDetail.email",          // assumption


        sourceLocationNameSO: "sourceDetail.locationName",
        sourceAddress1SO: "sourceDetail.addressLine1",
        sourceAddress2SO: "sourceDetail.addressLine2",
        sourceCitySO: "sourceDetail.city",
        sourceStateSO: "sourceDetail.state",
        sourceCountrySO: "sourceDetail.country",

        // ---------- Destination Address (destination) ----------
        destinationLocationName: "destination.locationName",
        destinationAddress1: "destination.addressLine1",
        destinationAddress2: "destination.addressLine2", // assumption
        destinationAddress3: "destination.addressLine3", // assumption
        destinationAddress4: "destination.addressLine4", // assumption
        destinationCity: "destination.city",
        destinationState: "destination.state",
        destinationCountry: "destination.country",
        destinationContact: "destination.contact",       // assumption
        destinationEmail: "destination.email",           // assumption

        // SO create form specific
        destinationLocationNameSO: "destination.locationName",
        destinationAddress1SO: "destination.addressLine1",
        destinationAddress2SO: "destination.addressLine2",
        destinationCitySO: "destination.city",
        destinationStateSO: "destination.state",
        destinationCountrySO: "destination.country",

        // ---------- Bill To (assumed structure: billTo.*) ----------
        billToName: "billTo.name",
        billToAddress1: "billTo.addressLine1",
        billToAddress2: "billTo.addressLine2",
        billToCity: "billTo.city",
        billToState: "billTo.state",
        billToCountry: "billTo.country",
        billToContact: "billTo.contact",
        billToEmail: "billTo.email",

        // ---------- Misc ----------
        notes: "misc.notes",
        termsOfDelivery: "misc.termsOfDelivery",
        termsOfPayment: "misc.termsOfPayment",

        // ---------- UDF (udf block) ----------
        // simple UDF set
        soStoHeaderUdf1: "udf.headerUdf1",
        soStoHeaderUdf2: "udf.headerUdf2",
        soStoTextUdf1: "udf.textUdf1",
        soStoTextUdf2: "udf.textUdf2",

        // extended UDF (formSections me)
        soStoHeaderUdf3: "udf.headerUdf3",
        soStoHeaderUdf4: "udf.headerUdf4",
        soStoTextUdf3: "udf.textUdf3",
        soStoTextUdf4: "udf.textUdf4",

        // ---------- Ids ----------
        _id: "_id",
    };


    // 🔹 Mapper: UI filters -> API filters for Sales Order
    function mapSoUiFiltersToApi(filters = []) {
        if (!Array.isArray(filters)) return [];

        return filters
            .map((f) => {
                if (!f || !f.columnId) return null;

                const apiField = SALES_ORDER_FILTER_FIELD_MAP[f.columnId] ?? f.columnId;


                if (apiField === null) return null;

                return {
                    columnId: apiField,
                    operator: f.operator,
                    value: f.value,
                };
            })
            .filter(Boolean);
    }


    const loadFromServer = async (pageNo, pageSizeNo, filter = []) => {

        console.log("loadFromServer called with salesorder", filter);
        // If parent provided initialRows, use them instead of fetching
        if (initialRows && Array.isArray(initialRows) && initialRows.length > 0) {
            const mapped = initialRows.map((r) =>
                typeof r === "object" ? r : mapApiItemToRow(r)
            );
            rowsRef.current = mapped;
            setRows(mapped);
            return;
        }

        setLoading(true);
        try {

            const apifilter = mapSoUiFiltersToApi(filter)
            const resp = await fetchSalesOrder(apifilter, pageNo, pageSizeNo).catch((err) => {
                if (err?.name === "AbortError") return null;
                throw err;
            });


            if (!resp) {
                setLoading(false);
                return;
            }
            const total = resp.pagination.total;

            const list = Array.isArray(resp.data)
                ? resp.data
                : resp?.data ?? resp?.items ?? resp?.result ?? [];
            const mapped = (list || []).map(mapApiItemToRow);

            // avoid unnecessary rerenders by comparing JSON (ok for moderate payloads)
            const prevJson = JSON.stringify(rowsRef.current);
            const newJson = JSON.stringify(mapped);

            if (prevJson !== newJson) {
                if (rafRef.current) cancelAnimationFrame(rafRef.current);
                rafRef.current = requestAnimationFrame(() => {
                    rowsRef.current = mapped;
                    setRows(mapped);
                });
            }

            setTotalCount(total);

        } catch (err) {
            if (err?.name !== "AbortError")
                console.error("Failed to fetch SO/STOs:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadFromServer(page, pageSize);
        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            if (abortRef.current) abortRef.current.abort?.();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialRows, page, pageSize]);

    const handleDeleteRow = (idOrDbId) => {
        // remove locally; ideally call delete API then reload
        setRows((prev) => prev.filter((r) => r.id !== idOrDbId && r._id !== idOrDbId));
    };

    const handleViewRow = (row) => {
        console.log("View row requested:", row);
        if (typeof parentOnViewRow === "function") {
            try {
                parentOnViewRow(row._raw ?? row, { readOnly: true, hideCreate: true });
            } catch (err) {
                console.warn("parentOnViewRow threw:", err);
            }
            if (typeof onToggleCreateVisibility === "function")
                onToggleCreateVisibility(false);
            return;
        }

        setSelectedRow(row);
        if (typeof onToggleCreateVisibility === "function")
            onToggleCreateVisibility(false);
        setViewOpen(true);
    };

    const handleCloseView = () => {
        setViewOpen(false);
        setSelectedRow(null);
        if (typeof onToggleCreateVisibility === "function")
            onToggleCreateVisibility(true);
    };

    // show actions only when any of handlers exist
    const showActions = Boolean(onEdit ?? onEditRow ?? parentOnViewRow);

    const allPossibleColumns = useMemo(() => {
        // Prepend actions column only if showActions
        if (showActions) {
            return [
                actionsCol(onEdit ?? onEditRow, handleDeleteRow, handleViewRow),
                ...ALL_COLUMNS,
            ];
        }
        return [...ALL_COLUMNS];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ALL_COLUMNS, onEdit, onEditRow, parentOnViewRow]);


    const onManualRefresh = useCallback(
        async ({ filters = [], page: p = page ?? 0, pageSize: ps = pageSize ?? 10 } = {}) => {
            try {
                // simply reuse your existing loader
                await loadFromServer(p, ps, filters);
            } catch (err) {
                console.error("Manual refresh failed:", err);
                toast.error(err?.message || "Refresh failed");
            }
        },
        [loadFromServer, page, pageSize]
    );

    // Sync WMS → POST salesorder/refresh-wms, then reload the current page.
    const handleSyncWms = useCallback(
        async ({ filters = [], page: p = page ?? 0, pageSize: ps = pageSize ?? 10 } = {}) => {
            await refreshSalesOrderWms();
            await loadFromServer(p, ps, filters);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [page, pageSize]
    );

    return (
        <>
            <CommonDataTable
                id="so-main"
                allPossibleColumns={allPossibleColumns}
                initialVisible={["actions", ...INITIAL_VISIBLE]}
                initialRows={rows}
                height={600}
                rowsPerPageOptions={[10, 25, 50, 100]}
                loading={loading}
                exportImportPath="salesorder"
                checkboxSelection
                showCreateButton={showCreateButton}
                createButtonText={createButtonText}
                createButtonIcon={createButtonIcon}
                onCreateClick={onCreateClick}
                // onDataRefresh={loadFromServer}
                forInventory={true}
                showSyncWms
                onSyncWms={handleSyncWms}

                linkColumns={{
                    shipmentHistory: {
                        entity: "Shipment",
                        parseValues: (val) => {
                            if (!val && val !== 0) return [];
                            if (Array.isArray(val)) {
                                return val
                                    .map((s) => (s?.shipmentNumber ?? s?.shipment ?? s))
                                    .filter(Boolean)
                                    .map(String);
                            }
                            return String(val)
                                .split(",")
                                .map((s) => s.trim())
                                .filter(Boolean);
                        },
                    },
                }}
                onLinkOpen={({ field, value /*, row*/ }) => {
                    if (field === "shipmentHistory" && value) {
                        // normalize to a single shipment number string
                        const numbers = Array.isArray(value)
                            ? value.map(v => (v?.shipmentNumber ?? v?.shipment ?? v)).filter(Boolean).map(String)
                            : String(value).split(",").map(s => s.trim()).filter(Boolean);

                        if (numbers.length === 0) return;
                        // open the first clicked shipment number; parent will fetch it
                        onOpenShipment?.(numbers[0]);
                    }
                }}
                page={page}
                pageSize={pageSize}
                rowCount={totalCount}
                onPageChange={(newPage) => setPage(newPage)}
                onPageSizeChange={(newSize) => {
                    setPageSize(newSize);
                    setPage(0);
                }}



                onDataRefresh={async ({ filters = [], page: p = 0, pageSize: ps = pageSize } = {}) => {
                    // NOTE: this just forwards the values to your fetchHandler. For now we console.log as requested.
                    console.log("Salesorder ....................... received onDataRefresh:", { filters, page: p, pageSize: ps });
                    // call your existing fetchHandler (we'll update it to accept filters)
                    // await loadList(p, ps, filters);
                    await loadFromServer(p, ps, filters);
                }}


            // onManualRefresh={onManualRefresh}

            />


            <Dialog open={viewOpen} onClose={handleCloseView} maxWidth="md" fullWidth>
                <DialogTitle>View SO/STO (Read-only)</DialogTitle>
                <DialogContent dividers>
                    {selectedRow ? (
                        <Grid container spacing={1}>
                            {allPossibleColumns
                                .filter((c) => c.field !== "actions")
                                .map((col) => (
                                    <React.Fragment key={col.field}>
                                        <Grid item xs={4}>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {col.headerName}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={8}>
                                            <Typography variant="body2">
                                                {String(selectedRow[col.field] ?? "")}
                                            </Typography>
                                        </Grid>
                                    </React.Fragment>
                                ))}

                            <Grid item xs={12} style={{ marginTop: 12 }}>
                                <Typography variant="subtitle2">Line Items</Typography>
                                <pre
                                    style={{
                                        whiteSpace: "pre-wrap",
                                        maxHeight: 240,
                                        overflow: "auto",
                                    }}
                                >
                                    {JSON.stringify(
                                        selectedRow.lineItems ??
                                        selectedRow._raw?.lineItems ??
                                        [],
                                        null,
                                        2
                                    )}
                                </pre>
                            </Grid>

                            <Grid item xs={12} style={{ marginTop: 12 }}>
                                <Typography variant="subtitle2">Raw Data</Typography>
                                <pre
                                    style={{
                                        whiteSpace: "pre-wrap",
                                        maxHeight: 240,
                                        overflow: "auto",
                                    }}
                                >
                                    {JSON.stringify(selectedRow._raw ?? selectedRow, null, 2)}
                                </pre>
                            </Grid>
                        </Grid>
                    ) : (
                        <Typography>No data to display</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseView}>Close</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
